import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react"
import { appSyncApiConfig as config } from "./config"
import { QueryResponse } from "shared/types/queryResponse"
// import type { Document } from "../../shared/types-exp"

// Define a service using a base URL and expected endpoints
export const documentsApi = createApi({
  reducerPath: "documentsApi",
  baseQuery: fetchBaseQuery({
    baseUrl: config.baseUrl,
    prepareHeaders: (headers) => {
      headers.set("x-api-key", config.apiKey)

      return headers
    },
  }),
  tagTypes: ["Document"],

  endpoints: (builder) => ({
    getDocument: builder.query<any, { countryId: string; id: string }>({
      query: (params) => ({
        url: "",
        method: "POST",
        body: {
          query: `
            query GetDocument($countryId: ID = "", $id: ID = "") {
              getDocument(countryId: $countryId, id: $id) {
                activeVersion
                author
                countryId
                enabled
                id
                lastUpdated
                name
                refId
                isIncludedInWorkflow
                openForChanges
                projectType
                documentVersions {
                  items {
                    access
                    documentId
                    id
                    refId
                    latestVersion
                    isGlobal
                    isCountry
                    isProject
                    documentRefId
                    users
                    sections {
                      id
                      refId
                      isProject
                      isCountry
                      isClientEditable
                      isGlobal
                      name
                      viewing
                      type
                    }
                    chapters {
                      items {
                        assignedProjectUser
                        chapterOrder
                        documentVersionId
                        id
                        access
                        editing {
                          name
                          email
                          surname
                          isEditing
                        }
                          epi_status {
                            status
                            lastUpdated
                            userLastUpdated
                          }
                          client_status {
                            status
                            lastUpdated
                            userLastUpdated
                          }
                        name
                        refId
                        parentId
                        sections {
                          content {
                            plainText
                            uploadedFiles {
                              id
                              name
                              size
                              type
                              content
                            }
                          }
                          id
                          refId
                          isClientEditable
                          isGlobal
                          isCountry
                          isProject
                          name
                          viewing
                          type
                        }
                        sharedId
                        isIncludedInWorkflow
                        subchapterCreateAllowed
                        treeIndex
                      }
                    }
                  }
                }
              }
            }
          `,
          variables: {
            countryId: params.countryId,
            id: params.id,
          },
        },
      }),
      providesTags: ["Document"],
    }),
    getDocumentForSyncAllAndStructure: builder.query<
      any,
      { countryId: string; id: string }
    >({
      query: (params) => ({
        url: "",
        method: "POST",
        body: {
          query: `
            query GetDocumentForSyncAllAndStructure($countryId: ID = "", $id: ID = "") {
              getDocument(countryId: $countryId, id: $id) {
                documentVersions {
                  items {
                    id
                    documentId
                    sections {
                      type
                      id
                      refId
                      isCountry
                      isProject
                      isClientEditable
                      isGlobal
                      name
                      viewing
                    }
                    chapters {
                      items {
                        id
                        name
                        refId
                        parentId
                        isProject
                        chapterOrder
                        documentVersionId
                        subchapterCreateAllowed
                      }
                    }
                  }
                }
              }
            }
          `,
          variables: {
            countryId: params.countryId,
            id: params.id,
          },
        },
      }),
      providesTags: ["Document"],
    }),
    listFullDocumentsByRefId: builder.query<any, string>({
      keepUnusedDataFor: 0,
      query: (refId) => ({
        url: "",
        method: "POST",
        body: {
          query: `
            query ListFullDocumentsByRefId($eq: String = "", $isProject: Boolean = false) {
              listDocuments(filter: {refId: {eq: $eq}, isProject: {eq: $isProject}}) {
                items {
                  id
                  countryId
                  documentVersions {
                    items {
                      documentId
                      id
                      chapters {
                        items {
                          documentVersionId
                          id
                          sections {
                            type
                            content {
                              plainText
                              uploadedFiles {
                                id
                                content
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          `,
          variables: {
            eq: refId,
          },
        },
      }),
    }),
    listDocumentsByRefIdForChapterReOrder: builder.query<any, string>({
      query: (refId) => ({
        url: "",
        method: "POST",
        body: {
          query: `
            query ListDocumentsByRefIdForChapterReOrder($eq: String = "") {
              listDocuments(filter: {refId: {eq: $eq}}) {
                items {
                  documentVersions {
                    items {
                      chapters {
                        items {
                          id
                          name
                          refId
                          access
                          sharedId
                          parentId
                          isGlobal
                          isProject
                          isCountry
                          treeIndex
                          chapterOrder
                          documentRefId
                          documentVersionId
                          assignedProjectUser
                          isIncludedInWorkflow
                          subchapterCreateAllowed
                          editing {
                            name
                            email
                            surname
                            isEditing
                          }
                          epi_status {
                            status
                            lastUpdated
                            userLastUpdated
                          }
                          client_status {
                            status
                            lastUpdated
                            userLastUpdated
                          }
                          sections {
                            content {
                              plainText
                              uploadedFiles {
                                id
                                name
                                size
                                type
                                content
                              }
                            }
                            id
                            name
                            type
                            refId
                            viewing
                            isProject
                            isCountry
                            isGlobal
                            isClientEditable
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          `,
          variables: {
            eq: refId,
          },
        },
      }),
      providesTags: ["Document"],
    }),
    listDocumentsByRefId: builder.query<any, string>({
      keepUnusedDataFor: 0,
      query: (refId) => ({
        url: "",
        method: "POST",
        body: {
          query: `
            query ListDocumentsByRefId($eq: String = "") {
              listDocuments(filter: {refId: {eq: $eq}}) {
                items {
                  activeVersion
                  author
                  countryId
                  enabled
                  lastUpdated
                  refId
                  name
                  id
                  isIncludedInWorkflow
                  openForChanges
                  projectType
                  isProject
                }
              }
            }
          `,
          variables: {
            eq: refId,
          },
        },
      }),
      providesTags: ["Document"],
    }),
    createDocument: builder.mutation<any, any>({
      query: (data) => ({
        url: "",
        method: "POST",
        body: {
          query: `
            mutation CreateDocument(
              $activeVersion: Int = 1, 
              $author: String = "", 
              $countryId: ID = "", 
              $enabled: Boolean = false, 
              $lastUpdated: String = "", 
              $isProject: Boolean = false,
              $name: String = "", 
              $refId: ID = "",
              $isIncludedInWorkflow: Boolean = false,
              $openForChanges: Boolean = true,
              $projectType: String = ""
            ) {
              createDocument(
                input: {
                  activeVersion: $activeVersion, 
                  author: $author, 
                  countryId: $countryId, 
                  isProject: $isProject,
                  enabled: $enabled, 
                  lastUpdated: $lastUpdated, 
                  name: $name, 
                  refId: $refId,
                  isIncludedInWorkflow: $isIncludedInWorkflow,
                  openForChanges: $openForChanges,
                  projectType: $projectType
                }
              ) {
                activeVersion
                author
                countryId
                isProject
                enabled
                id
                lastUpdated
                name
                refId
                isIncludedInWorkflow
                openForChanges
                projectType
              }
            }
          `,
          variables: {
            countryId: data.countryId,
            activeVersion: data.activeVersion,
            author: data.author,
            isProject: data.isProject,
            enabled: data.enabled,
            lastUpdated: data.lastUpdated,
            name: data.name,
            refId: data.refId,
            isIncludedInWorkflow: data.isIncludedInWorkflow,
            openForChanges: data.openForChanges,
            projectType: data.projectType,
          },
        },
      }),
      invalidatesTags: ["Document"],
    }),
    updateDocument: builder.mutation<any, any>({
      query: (data) => ({
        url: "",
        method: "POST",
        body: {
          query: `
            mutation UpdateDocument(
              $activeVersion: Int = 1, 
              $author: String = "", 
              $countryId: ID = "", 
              $enabled: Boolean = false, 
              $id: ID = "", 
              $lastUpdated: String = "",
              $isProject: Boolean = false,
              $name: String = "", 
              $refId: ID = "",
              $isIncludedInWorkflow: Boolean = false,
              $openForChanges: Boolean = true,
              $projectType: String = "",
            ) {
              updateDocument(
                input: {
                  activeVersion: $activeVersion, 
                  author: $author, 
                  countryId: $countryId,
                  isProject: $isProject,
                  enabled: $enabled, 
                  id: $id, 
                  lastUpdated: $lastUpdated, 
                  name: $name, 
                  refId: $refId,
                  isIncludedInWorkflow: $isIncludedInWorkflow,
                  openForChanges: $openForChanges,
                  projectType: $projectType,
                }
              ) {
                activeVersion
                author
                countryId
                enabled
                isProject
                id
                lastUpdated
                name
                refId
                isIncludedInWorkflow
                openForChanges
                projectType
              }
            }
          `,
          variables: {
            activeVersion: data.activeVersion,
            author: data.author,
            countryId: data.countryId,
            isProject: data.isProject,
            enabled: data.enabled,
            id: data.id,
            lastUpdated: data.lastUpdated,
            name: data.name,
            refId: data.refId,
            isIncludedInWorkflow: data.isIncludedInWorkflow,
            openForChanges: data.openForChanges,
            projectType: data.projectType,
          },
        },
      }),
      invalidatesTags: ["Document"],
    }),
    batchUpdateDocuments: builder.mutation<any, any>({
      query: (data) => ({
        url: "",
        method: "POST",
        body: {
          query: `
            mutation BatchUpdateDocuments($objects: [UpdateDocumentInput!]!) {
              batchUpdateDocuments(input: {objects: $objects}) {
                id
                countryId
              }
            }
          `,
          variables: {
            objects: data.objects,
          },
        },
      }),
      invalidatesTags: ["Document"],
    }),
    getDocumentVersionsFromDocumentV1: builder.query<
      QueryResponse<"getDocument">,
      { countryId: string; id: string }
    >({
      query: (params) => ({
        url: "",
        method: "POST",
        body: {
          query: `
            query GetDocument($countryId: ID = "", $id: ID = "") {
              getDocument(countryId: $countryId, id: $id) {
                documentVersions {
                  items {
                    documentId
                    id
                    latestVersion
                    isGlobal
                    isCountry
                    isProject
                    refId
                    documentRefId
                    users
                    sections {
                      id
                      refId
                      isProject
                      isCountry
                      isClientEditable
                      isGlobal
                      name
                      type
                      viewing
                    }
                    chapters {
                      items {
                          id
                          name
                          refId
                          access
                          sharedId
                          parentId
                          isGlobal
                          isProject
                          isCountry
                          treeIndex
                          chapterOrder
                          documentRefId
                          documentVersionId
                          assignedProjectUser
                          isIncludedInWorkflow
                          subchapterCreateAllowed
                          editing {
                            name
                            email
                            surname
                            isEditing
                          }
                          epi_status {
                            status
                            lastUpdated
                            userLastUpdated
                          }
                          client_status {
                            status
                            lastUpdated
                            userLastUpdated
                          }
                          sections {
                            content {
                              plainText
                              uploadedFiles {
                                id
                                name
                                size
                                type
                                content
                              }
                            }
                            id
                            name
                            type
                            refId
                            viewing
                            isProject
                            isCountry
                            isGlobal
                            isClientEditable
                          }
                      }
                    }
                  }
                }
              }
            }
          `,
          variables: {
            countryId: params.countryId,
            id: params.id,
          },
        },
      }),
      providesTags: ["Document"],
    }),
  }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetDocumentQuery,
  useCreateDocumentMutation,
  useUpdateDocumentMutation,
  useListDocumentsByRefIdQuery,
  useBatchUpdateDocumentsMutation,
  useListFullDocumentsByRefIdQuery,
  useGetDocumentForSyncAllAndStructureQuery,
  useListDocumentsByRefIdForChapterReOrderQuery,
  util: documentsUtil,
} = documentsApi
